// DEPLOY: /var/www/woody.wtf/frontend/src/components/PromptDiff.js
// Line diff of two prompt texts (longest common subsequence): removed lines red,
// added green, unchanged runs longer than CONTEXT*2 folded. Used by the AI Prompts
// page's version history and the AI Audit page's Accept panel.
import React, { useMemo } from 'react';

const CONTEXT = 2;

export function diffLines(a, b) {
  const x = (a || '').split('\n'), y = (b || '').split('\n');
  const n = x.length, m = y.length;
  const L = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      L[i][j] = x[i] === y[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && x[i] === y[j]) { out.push(['=', x[i]]); i++; j++; }
    else if (i < n && (j >= m || L[i + 1][j] >= L[i][j + 1])) out.push(['-', x[i++]]);   // removals first
    else out.push(['+', y[j++]]);
  }
  return out;
}

const ROW = {
  '+': { background: 'var(--success-dim)', color: 'var(--text)' },
  '-': { background: 'var(--error-dim)', color: 'var(--text-muted)', textDecoration: 'line-through' },
  '=': { color: 'var(--text-muted)' },
};

export default function PromptDiff({ from, to, maxHeight = '420px' }) {
  const rows = useMemo(() => diffLines(from, to), [from, to]);
  const added = rows.filter(r => r[0] === '+').length, removed = rows.filter(r => r[0] === '-').length;

  // Fold long unchanged runs to CONTEXT lines either side of a change.
  const shown = [];
  rows.forEach((r, k) => {
    const near = rows.slice(Math.max(0, k - CONTEXT), k + CONTEXT + 1).some(z => z[0] !== '=');
    if (r[0] !== '=' || near) shown.push(r);
    else if (shown.length && shown[shown.length - 1][0] !== '…') shown.push(['…', '']);
  });

  return (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px' }}>
        <span style={{ color: 'var(--success)' }}>+{added}</span> / <span style={{ color: 'var(--error)' }}>−{removed}</span> lines
        {!added && !removed && ' · identical'}
      </div>
      <div style={{ maxHeight, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)' }}>
        {shown.map(([op, line], k) => op === '…'
          ? <div key={k} style={{ padding: '2px 10px', fontSize: '11px', color: 'var(--text-muted)', background: 'var(--surface)' }}>⋯</div>
          : (
            <div key={k} style={{ ...ROW[op], display: 'flex', fontFamily: "'DM Mono', monospace", fontSize: '12px', lineHeight: 1.5 }}>
              <span style={{ width: '18px', flexShrink: 0, textAlign: 'center', userSelect: 'none', opacity: 0.7 }}>{op === '=' ? '' : op}</span>
              <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', paddingRight: '10px' }}>{line || ' '}</span>
            </div>
          ))}
      </div>
    </div>
  );
}
